/**
 * TruckStopsLayer - Map overlay showing nearby truck stops
 * 
 * Essential for oilfield workers driving semi trucks who need:
 * - Diesel fuel
 * - Truck parking
 * - Showers
 * - CAT scales
 * 
 * Shows Loves, Pilot, Flying J, TA Travel Centers, etc.
 */

import { useState, useEffect, useCallback } from 'react';

interface TruckStop {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  distance: number;
  distanceDisplay: string;
  rating?: number;
  totalRatings?: number;
  isOpen?: boolean;
  isOperational: boolean;
  icon: string;
  category: string;
}

interface TruckStopsLayerProps {
  map: google.maps.Map | null;
  userLocation: { lat: number; lng: number } | null;
  visible: boolean;
  darkMode: boolean;
  onTruckStopCountChange?: (count: number) => void;
  onLoadingChange?: (loading: boolean) => void;
}

// Custom SVG for truck stop markers
const createTruckStopMarkerSvg = (isOpen: boolean = true): string => {
  const bgColor = isOpen ? '#f59e0b' : '#6b7280'; // Amber for open, gray for closed
  const shadowColor = isOpen ? '#f59e0b' : '#374151';
  
  return `
    <svg width="48" height="56" viewBox="0 0 48 56" xmlns="http://www.w3.org/2000/svg">
      <!-- Drop shadow -->
      <ellipse cx="24" cy="52" rx="10" ry="4" fill="rgba(0,0,0,0.3)"/>
      
      <!-- Pin body -->
      <path d="M24 0C12.954 0 4 8.954 4 20c0 14.5 20 34 20 34s20-19.5 20-34c0-11.046-8.954-20-20-20z" 
            fill="${bgColor}" 
            stroke="#fff" 
            stroke-width="2"
            filter="drop-shadow(0 2px 4px ${shadowColor}80)"/>
      
      <!-- Truck icon (simplified) -->
      <g transform="translate(12, 10)">
        <!-- Truck body -->
        <rect x="0" y="8" width="18" height="10" rx="1" fill="white"/>
        <!-- Truck cab -->
        <rect x="18" y="11" width="6" height="7" rx="1" fill="white"/>
        <!-- Windshield -->
        <rect x="19" y="12" width="4" height="3" rx="0.5" fill="${bgColor}"/>
        <!-- Wheels -->
        <circle cx="5" cy="18" r="3" fill="white" stroke="${bgColor}" stroke-width="1"/>
        <circle cx="14" cy="18" r="3" fill="white" stroke="${bgColor}" stroke-width="1"/>
        <circle cx="21" cy="18" r="2.5" fill="white" stroke="${bgColor}" stroke-width="1"/>
      </g>
    </svg>
  `;
};

export function TruckStopsLayer({ 
  map, 
  userLocation, 
  visible, 
  darkMode,
  onTruckStopCountChange,
  onLoadingChange,
}: TruckStopsLayerProps) {
  const [truckStops, setTruckStops] = useState<TruckStop[]>([]);
  const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null);

  // Fetch truck stops from API
  const fetchTruckStops = useCallback(async () => {
    if (!userLocation) return;
    
    onLoadingChange?.(true);
    
    try {
      const response = await fetch(
        `/api/google/places?lat=${userLocation.lat}&lng=${userLocation.lng}&category=truckStop&radius=80467` // 50 miles
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch truck stops');
      }
      
      const data = await response.json();
      const stops = data.places || [];
      setTruckStops(stops);
      onTruckStopCountChange?.(stops.length);
    } catch (e) {
      console.error('Truck stops error:', e);
      setTruckStops([]);
      onTruckStopCountChange?.(0);
    } finally {
      onLoadingChange?.(false);
    }
  }, [userLocation, onTruckStopCountChange, onLoadingChange]);

  // Fetch when visibility changes or location changes
  useEffect(() => {
    if (visible && userLocation) {
      fetchTruckStops();
    }
  }, [visible, userLocation?.lat, userLocation?.lng, fetchTruckStops]);

  // Create/update markers when truck stops change
  useEffect(() => {
    if (!map || !visible) {
      // Clear markers when not visible
      markers.forEach(marker => {
        marker.map = null;
      });
      setMarkers([]);
      return;
    }

    // Clear existing markers
    markers.forEach(marker => {
      marker.map = null;
    });

    // Check if marker library is loaded
    if (!google.maps.marker?.AdvancedMarkerElement) {
      console.warn('Google Maps marker library not loaded yet');
      return;
    }

    // Create new markers
    const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    
    truckStops.forEach(stop => {
      // Create custom marker element
      const markerDiv = document.createElement('div');
      markerDiv.innerHTML = createTruckStopMarkerSvg(stop.isOpen);
      markerDiv.style.cursor = 'pointer';
      markerDiv.style.transform = 'translate(-50%, -100%)';
      
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: stop.lat, lng: stop.lng },
        content: markerDiv,
        title: stop.name,
      });
      
      // Click handler to show info
      marker.addListener('click', () => {
        // Create info window content
        const infoContent = document.createElement('div');
        infoContent.innerHTML = `
          <div style="
            min-width: 220px;
            padding: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: ${darkMode ? 'rgba(17, 24, 39, 0.98)' : '#fff'};
            border-radius: 12px;
          ">
            <div style="
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 8px;
            ">
              <div style="
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: ${stop.isOpen ? '#f59e0b' : '#6b7280'};
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                🚚
              </div>
              <div>
                <div style="
                  font-weight: 700;
                  font-size: 14px;
                  color: ${darkMode ? '#fff' : '#111'};
                ">${stop.name}</div>
                <div style="
                  font-size: 11px;
                  color: ${darkMode ? '#9ca3af' : '#666'};
                ">${stop.distanceDisplay} away</div>
              </div>
            </div>
            
            <div style="
              font-size: 12px;
              color: ${darkMode ? '#9ca3af' : '#666'};
              margin-bottom: 8px;
            ">${stop.address}</div>
            
            <div style="
              display: flex;
              gap: 8px;
              align-items: center;
              margin-bottom: 12px;
            ">
              ${stop.rating ? `
                <div style="
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  background: #fbbf2420;
                  padding: 4px 8px;
                  border-radius: 12px;
                ">
                  <span style="color: #fbbf24;">★</span>
                  <span style="font-size: 12px; color: ${darkMode ? '#fff' : '#111'}; font-weight: 600;">
                    ${stop.rating.toFixed(1)}
                  </span>
                </div>
              ` : ''}
              ${stop.isOpen !== undefined ? `
                <div style="
                  font-size: 11px;
                  padding: 4px 8px;
                  border-radius: 12px;
                  background: ${stop.isOpen ? '#10b98120' : '#ef444420'};
                  color: ${stop.isOpen ? '#10b981' : '#ef4444'};
                  font-weight: 600;
                ">
                  ${stop.isOpen ? 'Open Now' : 'Closed'}
                </div>
              ` : ''}
            </div>
            
            <div style="display: flex; gap: 6px; flex-wrap: wrap;">
              <button 
                onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}&travelmode=driving', '_blank')"
                style="
                  flex: 1;
                  min-width: 80px;
                  padding: 8px;
                  border: none;
                  border-radius: 8px;
                  background: linear-gradient(135deg, #4285F4 0%, #1a73e8 100%);
                  color: #fff;
                  font-weight: 700;
                  font-size: 11px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 4px;
                "
              >
                <span>🗺️</span> Google
              </button>
              <button 
                onclick="window.open('https://maps.apple.com/?daddr=${stop.lat},${stop.lng}&dirflg=d', '_blank')"
                style="
                  flex: 1;
                  min-width: 80px;
                  padding: 8px;
                  border: none;
                  border-radius: 8px;
                  background: linear-gradient(135deg, #333 0%, #111 100%);
                  color: #fff;
                  font-weight: 700;
                  font-size: 11px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 4px;
                "
              >
                <span>🍎</span> Apple
              </button>
              <button 
                onclick="window.open('https://waze.com/ul?ll=${stop.lat},${stop.lng}&navigate=yes', '_blank')"
                style="
                  flex: 1;
                  min-width: 80px;
                  padding: 8px;
                  border: none;
                  border-radius: 8px;
                  background: linear-gradient(135deg, #33ccff 0%, #00a0e8 100%);
                  color: #000;
                  font-weight: 700;
                  font-size: 11px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 4px;
                "
              >
                <span>👻</span> Waze
              </button>
            </div>
          </div>
        `;
        
        // Create or update info window
        if (infoWindow) {
          infoWindow.close();
        }
        
        const newInfoWindow = new google.maps.InfoWindow({
          content: infoContent,
          pixelOffset: new google.maps.Size(0, -40),
        });
        
        newInfoWindow.open(map, marker);
        setInfoWindow(newInfoWindow);
      });
      
      newMarkers.push(marker);
    });
    
    setMarkers(newMarkers);
    
    // Cleanup
    return () => {
      newMarkers.forEach(marker => {
        marker.map = null;
      });
    };
  }, [map, truckStops, visible, darkMode]);

  // Close info window when visibility changes
  useEffect(() => {
    if (!visible && infoWindow) {
      infoWindow.close();
      setInfoWindow(null);
    }
  }, [visible, infoWindow]);

  // This component doesn't render any React elements - it only manages map markers
  return null;
}

export default TruckStopsLayer;
